import { z } from 'zod';
import {
  FREE_TEXT_REGEX,
  FREE_TEXT_MESSAGE,
  NAME_MIN,
  NAME_MAX,
  BUSINESS_NAME_MAX,
  STREET_NAME_MAX,
  DOOR_NUMBER_MAX,
  APARTMENT_NUMBER_MAX,
  CORNER_MAX,
  EXTERNAL_ID_MAX,
} from './fieldRules';

export enum BranchStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const BRANCH_STATUS_LABELS = {
  [BranchStatus.ACTIVE]: 'Activo',
  [BranchStatus.INACTIVE]: 'Inactivo',
} as const;

export interface Branch {
  id: string;
  name: string;
  businessName: string;
  commerceId: string;
  status?: string;
  externalId?: string;
  externalBranchCode?: string | null;
  apiCode?: string | null;
  logo?: string;
  businessType?: string;
  businessKey?: string;
  url?: string;
  phone?: string | null;
  enabled?: boolean;
  addressId?: string;
  createdAt: string;
  updatedAt: string;
  commerceName?: string;
  fullAddress?: string;
  address?: {
    id: string;
    streetName: string;
    doorNumber: string;
    apartmentNumber?: string;
    corner?: string;
    zipCode?: string;
    lat?: string | number;
    long?: string | number;
    neighborhoodId?: string;
    neighborhood?: {
      id: string;
      name?: string;
      cityId?: string;
      city?: {
        id: string;
        name?: string;
        stateOrDepartmentId?: string;
        stateOrDepartment?: {
          id: string;
          name?: string;
        };
      };
    };
  };
  commerce?: {
    id: string;
    name: string;
    businessName: string;
  };
}

export type BranchApi = Branch;

export interface NewBranch {
  name: string;
  businessName: string;
  commerceId: string;
  neighborhoodId: string;
  streetName: string;
  doorNumber: string;
  externalId?: string;
  logo?: string;
  businessType?: string;
  businessKey?: string;
  url?: string;
  phone?: string | null;
  enabled?: boolean;
  status?: string;
  apartmentNumber?: string;
  corner?: string;
}

export interface UpdateBranch {
  id: string;
  name: string;
  businessName: string;
  commerceId: string;
  addressId?: string;
  neighborhoodId: string;
  streetName: string;
  doorNumber: string;
  externalId?: string;
  logo?: string;
  businessType?: string;
  businessKey?: string;
  url?: string;
  phone?: string | null;
  enabled?: boolean;
  status?: string;
  apartmentNumber?: string;
  corner?: string;
  lat?: string;
  long?: string;
}

export interface BranchFilters {
  name?: string;
  businessName?: string;
  externalId?: string;
  enabled?: boolean;
  commerceId?: string;
  commerceIds?: string[];
  cityId?: string;
  neighborhoodId?: string;
  stateOrDepartmentId?: string;
  countryId?: string;
}

const baseBranchValidationSchema = z.object({
  name: z
    .string()
    .min(NAME_MIN, `El nombre debe tener al menos ${NAME_MIN} caracteres`)
    .max(NAME_MAX, `El nombre no puede exceder ${NAME_MAX} caracteres`)
    .regex(FREE_TEXT_REGEX, `El nombre ${FREE_TEXT_MESSAGE.toLowerCase()}`),
  businessName: z
    .string()
    .min(1, 'La razón social es requerida')
    .max(
      BUSINESS_NAME_MAX,
      `La razón social no puede exceder ${BUSINESS_NAME_MAX} caracteres`
    )
    .regex(
      FREE_TEXT_REGEX,
      `La razón social ${FREE_TEXT_MESSAGE.toLowerCase()}`
    ),
  externalId: z
    .string()
    .max(50, 'El ID externo no puede exceder 50 caracteres')
    .optional(),
  enabled: z.boolean().default(true),
  commerceId: z.string().min(1, 'El comercio es requerido'),
});

export const branchSchema = baseBranchValidationSchema;
export const updateBranchSchema = baseBranchValidationSchema.partial();

export type BranchSchema = z.infer<typeof branchSchema>;
export type UpdateBranchSchema = z.infer<typeof updateBranchSchema>;

export const defaultBranchValues: BranchSchema = {
  name: '',
  businessName: '',
  externalId: '',
  enabled: true,
  commerceId: '',
};

const baseBranchFormSchema = baseBranchValidationSchema.extend({
  name: z
    .string()
    .min(NAME_MIN, `El nombre debe tener al menos ${NAME_MIN} caracteres`)
    .max(NAME_MAX, `El nombre no puede exceder ${NAME_MAX} caracteres`)
    .regex(FREE_TEXT_REGEX, `El nombre ${FREE_TEXT_MESSAGE.toLowerCase()}`),
  externalId: z
    .string()
    .max(
      EXTERNAL_ID_MAX,
      `El ID externo no puede exceder ${EXTERNAL_ID_MAX} caracteres`
    )
    .optional()
    .or(z.literal('')),
  logo: z
    .string()
    .url('La URL del logo debe ser válida')
    .optional()
    .or(z.literal('')),
  businessType: z
    .string()
    .max(50, 'El tipo de negocio no puede exceder 50 caracteres')
    .optional()
    .or(z.literal('')),
  businessKey: z
    .string()
    .max(50, 'La clave de negocio no puede exceder 50 caracteres')
    .optional()
    .or(z.literal('')),
  url: z.string().url('La URL debe ser válida').optional().or(z.literal('')),
  phone: z
    .string()
    .max(30, 'El teléfono no puede exceder 30 caracteres')
    .optional()
    .or(z.literal('')),
  streetName: z
    .string()
    .min(1, 'El nombre de la calle es requerido')
    .max(
      STREET_NAME_MAX,
      `El nombre de la calle no puede exceder ${STREET_NAME_MAX} caracteres`
    )
    .regex(
      FREE_TEXT_REGEX,
      `El nombre de la calle ${FREE_TEXT_MESSAGE.toLowerCase()}`
    ),
  doorNumber: z
    .string()
    .min(1, 'El número de puerta es requerido')
    .max(
      DOOR_NUMBER_MAX,
      `El número de puerta no puede exceder ${DOOR_NUMBER_MAX} caracteres`
    )
    .regex(
      FREE_TEXT_REGEX,
      `El número de puerta ${FREE_TEXT_MESSAGE.toLowerCase()}`
    ),
  neighborhoodId: z.string().optional().or(z.literal('')),
  apartmentNumber: z
    .string()
    .max(
      APARTMENT_NUMBER_MAX,
      `El apartamento no puede exceder ${APARTMENT_NUMBER_MAX} caracteres`
    )
    .optional()
    .or(z.literal('')),
  corner: z
    .string()
    .max(CORNER_MAX, `La esquina no puede exceder ${CORNER_MAX} caracteres`)
    .optional()
    .or(z.literal('')),
  countryId: z.string().optional().or(z.literal('')),
  stateId: z.string().min(1, 'El departamento es requerido'),
  cityId: z.string().min(1, 'La ciudad es requerida'),
});

export const createBranchFormSchema = baseBranchFormSchema;

export type CreateBranchFormSchema = z.infer<typeof createBranchFormSchema>;

export const defaultCreateBranchFormValues: CreateBranchFormSchema = {
  name: '',
  businessName: '',
  commerceId: '',
  externalId: '',
  logo: '',
  businessType: '',
  businessKey: '',
  url: '',
  phone: '',
  enabled: true,
  streetName: '',
  doorNumber: '',
  neighborhoodId: '',
  apartmentNumber: '',
  corner: '',
  countryId: '',
  stateId: '',
  cityId: '',
};

export const editBranchFormSchema = baseBranchFormSchema.extend({
  businessName: z
    .string()
    .min(1, 'La razón social es requerida')
    .max(100, 'La razón social no puede exceder 100 caracteres'),
  neighborhoodId: z.string().optional().or(z.literal('')),
  addressId: z.string().optional(),
});

export type EditBranchFormSchema = z.infer<typeof editBranchFormSchema>;

export const defaultEditBranchFormValues: EditBranchFormSchema = {
  name: '',
  businessName: '',
  commerceId: '',
  addressId: '',
  externalId: '',
  logo: '',
  businessType: '',
  businessKey: '',
  url: '',
  phone: '',
  enabled: true,
  streetName: '',
  doorNumber: '',
  neighborhoodId: '',
  apartmentNumber: '',
  corner: '',
  countryId: '',
  stateId: '',
  cityId: '',
};

export interface CreateBranchPayload {
  name: string;
  businessName: string;
  commerceId: string;
  addressId?: string;
  neighborhoodId: string;
  streetName: string;
  doorNumber: string;
  externalId?: string;
  logo?: string;
  businessType?: string;
  businessKey?: string;
  url?: string;
  phone?: string | null;
  enabled?: boolean;
  status?: string;
  apartmentNumber?: string;
  corner?: string;
  lat?: string;
  long?: string;
}
